import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import useScreenMobile from "hooks/useScreen";
import { useToast } from "components/Toaster/index";
import "./spacePermissionsTable.scss";

/**
 * Drop duplicate strings (case-insensitive), first occurrence wins.
 * @param {string[]} [items] Input list; empty or missing values are skipped.
 * @return {string[]} Deduplicated list in original order.
 */
const dedupeStringsPreserveOrder = (items) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = String(item || "").trim().toLowerCase();
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const SpacePermissionsTable = ({ searchQuery, onSearch, spaceOps, spaceName, spaceConfig, handleUpdateSpacePermissions }) => {
  const filterOptions = [
    "Allowed Countries",
    "Denied Countries",
    // "Allowed Regions",
    // "Denied Regions",
    "Allowed Ips",
    "Denied Ips",
    "Allowed File Types",
    "Denied File Types",
    "Max File Size"
    // "Max Space Size"
  ];
  const fileTypeOptions = dedupeStringsPreserveOrder([
    // Documents
    ".pdf", ".docx", ".txt", ".rtf", ".odt",
    // Spreadsheets / presentations
    ".xlsx", ".csv", ".pptx",
    // Images
    ".jpg", ".png", ".gif", ".webp", ".svg",
    // Archives
    ".zip", ".rar", ".7z", ".tar", ".gz",
    // Media
    ".mp3", ".mp4",
    // Common engineering / config
    ".json", ".log",
    // Executables / scripts / installers
    ".bat",
    ".bin",
    // Newysafe custom docs/sheets
    ".idoc", ".isheet"
  ]);
  const fileTypeToMimeTypeMap = {
    ".pdf": "application/pdf",
    ".doc": "application/msword",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".txt": "text/plain",
    ".rtf": "application/rtf",
    ".odt": "application/vnd.oasis.opendocument.text",
    ".xls": "application/vnd.ms-excel",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".csv": "text/csv",
    ".ppt": "application/vnd.ms-powerpoint",
    ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".gif": "image/gif",
    ".webp": "image/webp",
    ".svg": "image/svg+xml",
    ".zip": "application/zip",
    ".rar": "application/vnd.rar",
    ".7z": "application/x-7z-compressed",
    ".tar": "application/x-tar",
    ".gz": "application/gzip",
    ".mp3": "audio/mpeg",
    ".mp4": "video/mp4",
    ".avi": "video/x-msvideo",
    ".json": "application/json",
    ".xml": "application/xml",
    ".yaml": "text/yaml",
    ".yml": "application/x-yaml",
    ".log": "text/x-log",
    ".msi": "application/x-msi",
    ".bat": "application/x-bat",
    ".cmd": "application/cmd",
    ".com": "application/x-msdownload",
    ".scr": "application/x-msdownload",
    ".ps1": "application/x-powershell",
    ".vbs": "text/vbscript",
    ".js": "application/javascript",
    ".wsf": "text/xml",
    ".sh": "application/x-sh",
    ".bin": "application/octet-stream",
    ".run": "application/x-executable",
    ".app": "application/octet-stream",
    ".dmg": "application/x-apple-diskimage",
    ".pkg": "application/octet-stream",
    "mach-o": "application/x-mach-binary",
    ".idoc": "application/idoc",
    ".isheet": "application/isheet"
  };
  const mimeTypeToFileTypeMap = {
    "application/pdf": ".pdf",
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
    "text/plain": ".txt",
    "text/x-log": ".log",
    "application/rtf": ".rtf",
    "application/vnd.oasis.opendocument.text": ".odt",
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "text/csv": ".csv",
    "application/vnd.ms-powerpoint": ".ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "image/svg+xml": ".svg",
    "application/zip": ".zip",
    "application/vnd.rar": ".rar",
    "application/x-7z-compressed": ".7z",
    "application/x-tar": ".tar",
    "application/gzip": ".gz",
    "audio/mpeg": ".mp3",
    "video/mp4": ".mp4",
    "video/x-msvideo": ".avi",
    "application/json": ".json",
    "application/xml": ".xml",
    "text/yaml": ".yaml",
    "application/x-yaml": ".yml",
    "application/x-msi": ".msi",
    "application/x-bat": ".bat",
    "application/cmd": ".cmd",
    "application/x-msdownload": ".com",
    "application/x-powershell": ".ps1",
    "text/vbscript": ".vbs",
    "application/javascript": ".js",
    "text/javascript": ".js",
    "text/xml": ".wsf",
    "application/x-sh": ".sh",
    "text/x-shellscript": ".sh",
    "application/octet-stream": ".bin",
    "application/x-executable": ".run",
    "application/x-apple-diskimage": ".dmg",
    "application/x-mach-binary": "Mach-O",
    "application/idoc": ".idoc",
    "application/isheet": ".isheet"
  };
  const fileSizeOptions = [
    "5 MB",
    "10 MB",
    "25 MB",
    "50 MB",
    "100 MB",
    "250 MB",
    "500 MB",
    "1 GB",
    "2 GB",
    "5 GB"
  ];
  const spaceSizeOptions = ["100 MB", "500 MB", "1 GB", "5 GB", "10 GB", "50 GB"];
  const [countryRegionsMap, setCountryRegionsMap] = useState({});

  // Console log to verify which space's permissions are being displayed
  // console.log("🔍 SpacePermissionsTable - Current Space:", {
  //   spaceName: spaceName || "No space selected",
  //   spaceOps: spaceOps || [],
  //   hasRead: spaceOps && spaceOps.includes("read"),
  //   hasWrite: spaceOps && spaceOps.includes("write")
  // });

  //  if spaceops array has "read",then set read to true for all 
  //  if spaceops array has "write",then set write to true for all permissions
  const getInitialRead = () => {
    return spaceOps && spaceOps.includes("read");
  };

  const getInitialWrite = () => {
    return spaceOps && spaceOps.includes("write");
  };

  const [permissions, setPermissions] = useState([
    { id: 1, name: "Files and Folders Management", read: getInitialRead(), write: getInitialWrite() },
    // { id: 2, name: "File Access", read: true, write: true, create: false },
    // { id: 3, name: "System Settings", read: true, write: false, create: false },
    // { id: 4, name: "Data Export", read: true, write: false, create: true },
    // { id: 5, name: "Audit Logs", read: true, write: false, create: false },
    // { id: 6, name: "Security Policies", read: true, write: true, create: true },
    // { id: 7, name: "Backup Management", read: true, write: false, create: false },
    // { id: 8, name: "API Access", read: true, write: false, create: false },
    // { id: 9, name: "Notification Settings", read: true, write: true, create: false },
    // { id: 10, name: "Role Management", read: true, write: false, create: false },
    // { id: 11, name: "Data Backup", read: true, write: false, create: false },
    // { id: 12, name: "System Monitoring", read: true, write: false, create: false },
    // { id: 13, name: "Access Control", read: true, write: true, create: true }
  ]);


  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFilterOptions, setSelectedFilterOptions] = useState([]);
  const [topDropdownSearch, setTopDropdownSearch] = useState("");
  const dropdownRef = useRef(null);
  const dropdownTriggerRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [topDropdownMenuStyle, setTopDropdownMenuStyle] = useState({});
  const [isAllowedCountriesDropdownOpen, setIsAllowedCountriesDropdownOpen] = useState(false);
  const [selectedAllowedCountries, setSelectedAllowedCountries] = useState([]);
  const [allowedCountriesSearch, setAllowedCountriesSearch] = useState("");
  const allowedCountriesDropdownRef = useRef(null);
  const allowedCountriesTriggerRef = useRef(null);
  const allowedCountriesMenuRef = useRef(null);
  const [allowedCountriesMenuStyle, setAllowedCountriesMenuStyle] = useState({});
  const [isDeniedCountriesDropdownOpen, setIsDeniedCountriesDropdownOpen] = useState(false);
  const [selectedDeniedCountries, setSelectedDeniedCountries] = useState([]);
  const [deniedCountriesSearch, setDeniedCountriesSearch] = useState("");
  const deniedCountriesDropdownRef = useRef(null);
  const deniedCountriesTriggerRef = useRef(null);
  const deniedCountriesMenuRef = useRef(null);
  const [deniedCountriesMenuStyle, setDeniedCountriesMenuStyle] = useState({});
  const [allowedIpsInput, setAllowedIpsInput] = useState("");
  const [isAllowedRegionsDropdownOpen, setIsAllowedRegionsDropdownOpen] = useState(false);
  const [selectedAllowedRegions, setSelectedAllowedRegions] = useState([]);
  const [allowedRegionsSearch, setAllowedRegionsSearch] = useState("");
  const allowedRegionsTriggerRef = useRef(null);
  const allowedRegionsMenuRef = useRef(null);
  const [allowedRegionsMenuStyle, setAllowedRegionsMenuStyle] = useState({});
  const [isDeniedRegionsDropdownOpen, setIsDeniedRegionsDropdownOpen] = useState(false);
  const [selectedDeniedRegions, setSelectedDeniedRegions] = useState([]);
  const [deniedRegionsSearch, setDeniedRegionsSearch] = useState("");
  const deniedRegionsTriggerRef = useRef(null);
  const deniedRegionsMenuRef = useRef(null);
  const [deniedRegionsMenuStyle, setDeniedRegionsMenuStyle] = useState({});
  const [deniedIpsInput, setDeniedIpsInput] = useState("");
  const [isAllowedFileTypesDropdownOpen, setIsAllowedFileTypesDropdownOpen] = useState(false);
  const [selectedAllowedFileTypes, setSelectedAllowedFileTypes] = useState([]);
  const [allowedFileTypesSearch, setAllowedFileTypesSearch] = useState("");
  const allowedFileTypesTriggerRef = useRef(null);
  const allowedFileTypesMenuRef = useRef(null);
  const [allowedFileTypesMenuStyle, setAllowedFileTypesMenuStyle] = useState({});
  const [isDeniedFileTypesDropdownOpen, setIsDeniedFileTypesDropdownOpen] = useState(false);
  const [selectedDeniedFileTypes, setSelectedDeniedFileTypes] = useState([]);
  const [deniedFileTypesSearch, setDeniedFileTypesSearch] = useState("");
  const deniedFileTypesTriggerRef = useRef(null);
  const deniedFileTypesMenuRef = useRef(null);
  const [deniedFileTypesMenuStyle, setDeniedFileTypesMenuStyle] = useState({});
  const [openCountryPicker, setOpenCountryPicker] = useState(null); // { kind: "allowed" | "denied", idx: number } | null
  const [countryPickerSearch, setCountryPickerSearch] = useState("");
  const [countryPickerMenuStyle, setCountryPickerMenuStyle] = useState({});
  const countryPickerMenuRef = useRef(null);
  const countryPickerTriggerRefs = useRef({});
  const [openRegionsPicker, setOpenRegionsPicker] = useState(null); // { kind: "allowed" | "denied", idx: number } | null
  const [regionsPickerSearch, setRegionsPickerSearch] = useState("");
  const [regionsPickerMenuStyle, setRegionsPickerMenuStyle] = useState({});
  const regionsPickerMenuRef = useRef(null);
  const regionsPickerTriggerRefs = useRef({});
  const [isMaxFileSizeDropdownOpen, setIsMaxFileSizeDropdownOpen] = useState(false);
  const [selectedMaxFileSize, setSelectedMaxFileSize] = useState("");
  const maxFileSizeTriggerRef = useRef(null);
  const maxFileSizeMenuRef = useRef(null);
  const [maxFileSizeMenuStyle, setMaxFileSizeMenuStyle] = useState({});
  const [isMaxSpaceSizeDropdownOpen, setIsMaxSpaceSizeDropdownOpen] = useState(false);
  const [selectedMaxSpaceSize, setSelectedMaxSpaceSize] = useState("");
  const maxSpaceSizeTriggerRef = useRef(null);
  const maxSpaceSizeMenuRef = useRef(null);
  const [maxSpaceSizeMenuStyle, setMaxSpaceSizeMenuStyle] = useState({});
  const showToast = useToast();

  // Update permissions when spaceOps or spaceName changes
  useEffect(() => {
    const newRead = spaceOps && spaceOps.includes("read");
    const newWrite = spaceOps && spaceOps.includes("write");

    // console.log("📋 SpacePermissionsTable - Updating permissions for space:", {
    //   spaceName: spaceName || "No space selected",
    //   spaceOps: spaceOps || [],
    //   newRead: newRead,
    //   newWrite: newWrite,
    //   timestamp: new Date().toISOString()
    // });

    setPermissions(prev => {
      const updated = prev.map(perm => ({
        ...perm,
        read: newRead,
        write: newWrite
      }));

      // console.log("✅ SpacePermissionsTable - Permissions updated:", {
      //   spaceName: spaceName || "No space selected",
      //   permissions: updated
      // });

      return updated;
    });

    // Reset unsaved changes when space changes
    setHasUnsavedChanges(false);
  }, [spaceOps, spaceName]);

  useEffect(() => {
    const parseCsvRow = (line) => {
      const values = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === "\"") {
          if (inQuotes && nextChar === "\"") {
            current += "\"";
            i += 1;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          values.push(current);
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current);
      return values.map((v) => v.trim());
    };

    const loadCountryRegionsFromCsv = async () => {
      try {
        const response = await fetch("/data/countries_regions.csv");
        if (!response.ok) {
          return;
        }
        const csvText = await response.text();
        const lines = csvText
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);

        if (lines.length <= 1) return;

        const parsedMap = {};
        for (let i = 1; i < lines.length; i += 1) {
          const row = parseCsvRow(lines[i]);
          if (row.length < 2) continue;
          const country = row[0];
          const region = row[1];
          if (!country || !region) continue;

          if (!parsedMap[country]) {
            parsedMap[country] = [];
          }
          if (!parsedMap[country].includes(region)) {
            parsedMap[country].push(region);
          }
        }

        if (Object.keys(parsedMap).length > 0) {
          setCountryRegionsMap(parsedMap);
          console.log("Loaded country-region mapping from /data/countries_regions.csv");
        }
      } catch (error) {
        console.error("Failed to load countries_regions.csv:", error);
      }
    };

    loadCountryRegionsFromCsv();
  }, []);

  const handlePermissionChange = (permissionId, action) => {
    setPermissions(prev => prev.map(perm =>
      perm.id === permissionId
        ? { ...perm, [action]: !perm[action] }
        : perm
    ));
    setHasUnsavedChanges(true);
  };

  const handleSavePermissions = async () => {
    if (!spaceName) {
      showToast("Please create or select a space before saving permissions.", "error");
      return;
    }
    setIsSaving(true);
    try {
      // Simulate API call to save permissions

      // Here you would typically make an API call to save the permissions
      const filePermissions = permissions[0];
      const spaceOpsToSave = [];
      if (filePermissions.read) {
        spaceOpsToSave.push("read");
      }
      if (filePermissions.write) {
        spaceOpsToSave.push("write");
      }

      // console.log("💾 SpacePermissionsTable - Saving permissions for space:", {
      //   spaceName: spaceName || "No space selected",
      //   spaceOpsToSave: spaceOpsToSave,
      //   permissions: filePermissions
      // });

      await handleUpdateSpacePermissions(
        spaceName,
        spaceOpsToSave,
        parseIpsInput(allowedIpsInput),
        parseIpsInput(deniedIpsInput),
        selectedAllowedCountries.filter(Boolean),
        selectedDeniedCountries.filter(Boolean),
        selectedAllowedRegions,
        selectedDeniedRegions,
        mapFileTypesToMimeTypes(selectedAllowedFileTypes),
        mapFileTypesToMimeTypes(selectedDeniedFileTypes),
        parseSizeToBytes(selectedMaxSpaceSize),
        parseSizeToBytes(selectedMaxFileSize)
      );
      console.log("SpacePermissionsTable save payload sent:", {
        spaceName,
        operations: spaceOpsToSave,
        allowedIps: parseIpsInput(allowedIpsInput),
        deniedIps: parseIpsInput(deniedIpsInput),
        allowedCountries: selectedAllowedCountries.filter(Boolean),
        deniedCountries: selectedDeniedCountries.filter(Boolean),
        allowedRegions: selectedAllowedRegions,
        deniedRegions: selectedDeniedRegions,
        allowedFileTypes: mapFileTypesToMimeTypes(selectedAllowedFileTypes),
        deniedFileTypes: mapFileTypesToMimeTypes(selectedDeniedFileTypes),
        maxSpaceSizeBytes: parseSizeToBytes(selectedMaxSpaceSize),
        maxFileSizeBytes: parseSizeToBytes(selectedMaxFileSize)
      });

      // console.log("✅ SpacePermissionsTable - Permissions saved successfully for space:", spaceName || "No space selected");
      // await new Promise(resolve => setTimeout(resolve, 1000));

      //  using setpermissions update the permissions to have updated spaceops
      // setPermissions(prevPermissions =>
      //   prevPermissions.map(perm => {
      //     if (perm.id === filePermissions.id) {
      //       return { ...perm, read: filePermissions.read, write: filePermissions.write };
      //     }
      //     return perm;
      //   })
      // );

      // Example API call:
      // await api.saveSpacePermissions(spaceId, permissions);

      // Reset unsaved changes flag
      setHasUnsavedChanges(false);
      showToast("Space permissions saved successfully", "success");

      // You could add a success toast here
    } catch (error) {
      console.error("Error saving permissions:", error);
      showToast(error?.message || "Failed to save space permissions", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPermissions = permissions.filter(permission =>
    permission.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const isOnTopDropdownMenu = dropdownMenuRef.current
          && dropdownMenuRef.current.contains(event.target);
        if (!isOnTopDropdownMenu) {
          setIsDropdownOpen(false);
        }
      }
      const isOnAllowedCountriesTrigger = allowedCountriesTriggerRef.current
        && allowedCountriesTriggerRef.current.contains(event.target);
      const isOnAllowedCountriesMenu = allowedCountriesMenuRef.current
        && allowedCountriesMenuRef.current.contains(event.target);
      const isOnDeniedCountriesTrigger = deniedCountriesTriggerRef.current
        && deniedCountriesTriggerRef.current.contains(event.target);
      const isOnDeniedCountriesMenu = deniedCountriesMenuRef.current
        && deniedCountriesMenuRef.current.contains(event.target);
      const isOnAllowedRegionsTrigger = allowedRegionsTriggerRef.current
        && allowedRegionsTriggerRef.current.contains(event.target);
      const isOnAllowedRegionsMenu = allowedRegionsMenuRef.current
        && allowedRegionsMenuRef.current.contains(event.target);
      const isOnDeniedRegionsTrigger = deniedRegionsTriggerRef.current
        && deniedRegionsTriggerRef.current.contains(event.target);
      const isOnDeniedRegionsMenu = deniedRegionsMenuRef.current
        && deniedRegionsMenuRef.current.contains(event.target);
      const isOnAllowedFileTypesTrigger = allowedFileTypesTriggerRef.current
        && allowedFileTypesTriggerRef.current.contains(event.target);
      const isOnAllowedFileTypesMenu = allowedFileTypesMenuRef.current
        && allowedFileTypesMenuRef.current.contains(event.target);
      const isOnDeniedFileTypesTrigger = deniedFileTypesTriggerRef.current
        && deniedFileTypesTriggerRef.current.contains(event.target);
      const isOnDeniedFileTypesMenu = deniedFileTypesMenuRef.current
        && deniedFileTypesMenuRef.current.contains(event.target);
      const isOnMaxFileSizeTrigger = maxFileSizeTriggerRef.current
        && maxFileSizeTriggerRef.current.contains(event.target);
      const isOnMaxFileSizeMenu = maxFileSizeMenuRef.current
        && maxFileSizeMenuRef.current.contains(event.target);
      const isOnMaxSpaceSizeTrigger = maxSpaceSizeTriggerRef.current
        && maxSpaceSizeTriggerRef.current.contains(event.target);
      const isOnMaxSpaceSizeMenu = maxSpaceSizeMenuRef.current
        && maxSpaceSizeMenuRef.current.contains(event.target);
      const isOnCountryPickerMenu = countryPickerMenuRef.current
        && countryPickerMenuRef.current.contains(event.target);
      const isOnCountryPickerTrigger = Boolean(event.target.closest(".CountryPickerTrigger"));
      const isOnRegionsPickerMenu = regionsPickerMenuRef.current
        && regionsPickerMenuRef.current.contains(event.target);
      const isOnRegionsPickerTrigger = Boolean(event.target.closest(".RegionsPickerTrigger"));

      if (!isOnAllowedCountriesTrigger && !isOnAllowedCountriesMenu) {
        setIsAllowedCountriesDropdownOpen(false);
      }
      if (!isOnDeniedCountriesTrigger && !isOnDeniedCountriesMenu) {
        setIsDeniedCountriesDropdownOpen(false);
      }
      if (!isOnAllowedRegionsTrigger && !isOnAllowedRegionsMenu) {
        setIsAllowedRegionsDropdownOpen(false);
      }
      if (!isOnDeniedRegionsTrigger && !isOnDeniedRegionsMenu) {
        setIsDeniedRegionsDropdownOpen(false);
      }
      if (!isOnAllowedFileTypesTrigger && !isOnAllowedFileTypesMenu) {
        setIsAllowedFileTypesDropdownOpen(false);
      }
      if (!isOnDeniedFileTypesTrigger && !isOnDeniedFileTypesMenu) {
        setIsDeniedFileTypesDropdownOpen(false);
      }
      if (!isOnMaxFileSizeTrigger && !isOnMaxFileSizeMenu) {
        setIsMaxFileSizeDropdownOpen(false);
      }
      if (!isOnMaxSpaceSizeTrigger && !isOnMaxSpaceSizeMenu) {
        setIsMaxSpaceSizeDropdownOpen(false);
      }
      if (!isOnCountryPickerTrigger && !isOnCountryPickerMenu) {
        setOpenCountryPicker(null);
        setCountryPickerSearch("");
      }
      if (!isOnRegionsPickerTrigger && !isOnRegionsPickerMenu) {
        setOpenRegionsPicker(null);
        setRegionsPickerSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openCountryPickerFor = (kind, idx) => {
    const key = `${kind}-${idx}`;
    const el = countryPickerTriggerRefs.current[key];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCountryPickerMenuStyle({
      position: "fixed",
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 260),
      zIndex: 9999
    });
    setOpenCountryPicker({ kind, idx });
  };

  const openRegionsPickerFor = (kind, idx) => {
    const key = `${kind}-${idx}`;
    const el = regionsPickerTriggerRefs.current[key];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setRegionsPickerMenuStyle({
      position: "fixed",
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 300),
      zIndex: 9999
    });
    setOpenRegionsPicker({ kind, idx });
  };

  const toggleFilterOption = (option) => {
    setSelectedFilterOptions((prev) => {
      const isRemoving = prev.includes(option);
      const next = isRemoving
        ? prev.filter((item) => item !== option)
        : [...prev, option];

      // If a filter row is removed, clear that row's value
      // so save payload does not keep stale data.
      if (isRemoving) {
        if (option === "Allowed Countries") setSelectedAllowedCountries([]);
        if (option === "Denied Countries") setSelectedDeniedCountries([]);
        if (option === "Allowed Regions") setSelectedAllowedRegions([]);
        if (option === "Denied Regions") setSelectedDeniedRegions([]);
        if (option === "Allowed Ips") setAllowedIpsInput("");
        if (option === "Denied Ips") setDeniedIpsInput("");
        if (option === "Allowed File Types") setSelectedAllowedFileTypes([]);
        if (option === "Denied File Types") setSelectedDeniedFileTypes([]);
        if (option === "Max File Size") setSelectedMaxFileSize("");
        if (option === "Max Space Size") setSelectedMaxSpaceSize("");
      }

      return next;
    });
    setHasUnsavedChanges(true);
  };
  const getAdaptiveMenuStyle = (rect, minWidth = 240, menuHeight = 260) => {
    const viewportHeight = window.innerHeight || 0;
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom);
    const spaceAbove = Math.max(0, rect.top);
    const shouldOpenUp = spaceBelow < menuHeight + 12 && spaceAbove > spaceBelow;

    return {
      position: "fixed",
      top: shouldOpenUp
        ? Math.max(8, rect.top - menuHeight - 6)
        : rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, minWidth),
      zIndex: 9999
    };
  };
  const updateTopDropdownMenuPosition = () => {
    if (!dropdownTriggerRef.current) return;
    const rect = dropdownTriggerRef.current.getBoundingClientRect();
    setTopDropdownMenuStyle(getAdaptiveMenuStyle(rect, 220));
  };

  const toggleAllowedCountry = (country) => {
    if (selectedDeniedCountries.includes(country)) return;
    setSelectedAllowedCountries((prev) =>
      prev.includes(country) ? prev.filter((item) => item !== country) : [...prev, country]
    );
    setHasUnsavedChanges(true);
  };
  const toggleDeniedCountry = (country) => {
    if (selectedAllowedCountries.includes(country)) return;
    setSelectedDeniedCountries((prev) =>
      prev.includes(country) ? prev.filter((item) => item !== country) : [...prev, country]
    );
    setHasUnsavedChanges(true);
  };

  const handleAllowedCountrySlotChange = (slotIndex, newCountry) => {
    setSelectedAllowedCountries((prev) => {
      // When there are no slots in state yet, our UI renders a placeholder.
      if (prev.length === 0) {
        return newCountry ? [newCountry] : [];
      }

      const next = [...prev];
      if (next[slotIndex] === newCountry) return prev;
      next[slotIndex] = newCountry;
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const handleDeniedCountrySlotChange = (slotIndex, newCountry) => {
    setSelectedDeniedCountries((prev) => {
      // When there are no slots in state yet, our UI renders a placeholder.
      if (prev.length === 0) {
        return newCountry ? [newCountry] : [];
      }

      const next = [...prev];
      if (next[slotIndex] === newCountry) return prev;
      next[slotIndex] = newCountry;
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const handleAddAllowedCountrySlot = () => {
    // "+" adds a new single-select slot; doesn't change the payload until a country is picked.
    setSelectedAllowedCountries((prev) => [...prev, ""]);
  };

  const handleAddDeniedCountrySlot = () => {
    // "+" adds a new single-select slot; doesn't change the payload until a country is picked.
    setSelectedDeniedCountries((prev) => [...prev, ""]);
  };
  const toggleAllowedRegion = (region) => {
    if (selectedDeniedRegions.includes(region)) return;
    setSelectedAllowedRegions((prev) =>
      prev.includes(region) ? prev.filter((item) => item !== region) : [...prev, region]
    );
    setHasUnsavedChanges(true);
  };
  const toggleDeniedRegion = (region) => {
    if (selectedAllowedRegions.includes(region)) return;
    setSelectedDeniedRegions((prev) =>
      prev.includes(region) ? prev.filter((item) => item !== region) : [...prev, region]
    );
    setHasUnsavedChanges(true);
  };
  const toggleAllowedFileType = (type) => {
    if (selectedDeniedFileTypes.includes(type)) return;
    setSelectedAllowedFileTypes((prev) =>
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
    );
    setHasUnsavedChanges(true);
  };
  const toggleDeniedFileType = (type) => {
    if (selectedAllowedFileTypes.includes(type)) return;
    setSelectedDeniedFileTypes((prev) =>
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
    );
    setHasUnsavedChanges(true);
  };
  const availableCountries = useMemo(
    () => Object.keys(countryRegionsMap).sort((a, b) => a.localeCompare(b)),
    [countryRegionsMap]
  );
  const filteredTopOptions = useMemo(
    () => filterOptions.filter((option) => option.toLowerCase().includes(topDropdownSearch.toLowerCase())),
    [filterOptions, topDropdownSearch]
  );
  const filteredAllowedCountries = useMemo(
    () => availableCountries.filter((country) => country.toLowerCase().includes(allowedCountriesSearch.toLowerCase())),
    [availableCountries, allowedCountriesSearch]
  );
  const filteredDeniedCountries = useMemo(
    () => availableCountries.filter((country) => country.toLowerCase().includes(deniedCountriesSearch.toLowerCase())),
    [availableCountries, deniedCountriesSearch]
  );
  const parseIpsInput = (value) => {
    // Supported format:
    // - IPv4: x.x.x.x (0-255 each), optionally with CIDR (/0-32)
    const ipv4Octet = "(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)";
    const ipv4 = `${ipv4Octet}(?:\\.${ipv4Octet}){3}`;
    const ipv4Prefix = "(?:3[0-2]|[12]?\\d|\\d)"; // 0-32

    const ipv4Regex = new RegExp(`^${ipv4}$`);
    const ipv4CidrRegex = new RegExp(`^${ipv4}/${ipv4Prefix}$`);

    const isValidIpToken = (token) => {
      const t = String(token || "").trim();
      if (!t) return false;
      return ipv4Regex.test(t) || ipv4CidrRegex.test(t);
    };

    return String(value || "")
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .filter(isValidIpToken);
  };
  const sanitizeIpv4Input = (value) => String(value || "").replace(/[^0-9.,/\n]/g, "");
  const isValidIpv4DraftToken = (token) => {
    if (!token) return true;

    const parts = token.split("/");
    if (parts.length > 2) return false;

    const ipPart = parts[0];
    const prefixPart = parts[1];
    const octets = ipPart.split(".");

    if (octets.length > 4) return false;

    for (let i = 0; i < octets.length; i += 1) {
      const octet = octets[i];
      if (octet === "") continue; // allow progressive typing like "192." or "10.0."
      if (!/^\d{1,3}$/.test(octet)) return false;
      if (Number(octet) > 255) return false;
    }

    if (prefixPart !== undefined) {
      // CIDR is allowed only after full IPv4 address
      if (octets.length !== 4 || octets.some((octet) => octet === "")) return false;
      if (prefixPart !== "") {
        if (!/^\d{1,2}$/.test(prefixPart)) return false;
        if (Number(prefixPart) > 32) return false;
      }
    }

    return true;
  };
  const isValidIpv4DraftInput = (value) =>
    String(value || "")
      .split(/[,\n]/)
      .every((token) => isValidIpv4DraftToken(token.trim()));

  const parseSizeToBytes = (value) => {
    if (!value) return undefined;
    const [numRaw, unitRaw] = value.split(" ");
    const number = Number(numRaw);
    if (Number.isNaN(number)) return undefined;
    const unit = (unitRaw || "").toUpperCase();
    const multipliers = {
      KB: 1024,
      MB: 1024 * 1024,
      GB: 1024 * 1024 * 1024
    };
    const multiplier = multipliers[unit];
    if (!multiplier) return undefined;
    return Math.round(number * multiplier);
  };
  const formatBytesToSizeLabel = (bytesValue, options) => {
    const bytes = Number(bytesValue);
    if (!bytes || Number.isNaN(bytes)) return "";
    const candidates = [
      { label: `${bytes / (1024 * 1024 * 1024)} GB`, factor: 1024 * 1024 * 1024 },
      { label: `${bytes / (1024 * 1024)} MB`, factor: 1024 * 1024 }
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      const raw = bytes / candidate.factor;
      const rounded = Number.isInteger(raw) ? raw.toString() : raw.toFixed(2).replace(/\.?0+$/, "");
      const label = `${rounded} ${candidate.label.split(" ")[1]}`;
      if (options.includes(label)) {
        return label;
      }
    }
    return "";
  };
  const mapFileTypesToMimeTypes = (fileTypes = []) => {
    return dedupeStringsPreserveOrder(fileTypes).map((fileType) => {
      const normalized = (fileType || "").trim().toLowerCase();
      return fileTypeToMimeTypeMap[normalized] || fileType;
    });
  };
  const mapMimeTypesToFileTypes = (mimeTypes = []) => {
    return dedupeStringsPreserveOrder(mimeTypes).map((mimeType) => {
      const normalized = (mimeType || "").trim().toLowerCase();
      return mimeTypeToFileTypeMap[normalized] || mimeType;
    });
  };
  const normalizeCountryKey = (country) => {
    return (country || "").trim();
  };
  const canonicalCountryKey = (country) => {
    return normalizeCountryKey(country)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };
  const resolveCountryKey = useCallback((country) => {
    const normalizedCountry = normalizeCountryKey(country);
    if (!normalizedCountry) return "";

    if (countryRegionsMap[normalizedCountry]) {
      return normalizedCountry;
    }

    const directCaseInsensitive = Object.keys(countryRegionsMap).find(
      (key) => key.toLowerCase() === normalizedCountry.toLowerCase()
    );
    if (directCaseInsensitive) {
      return directCaseInsensitive;
    }

    const canonicalSelected = canonicalCountryKey(normalizedCountry);
    const canonicalMatch = Object.keys(countryRegionsMap).find(
      (key) => canonicalCountryKey(key) === canonicalSelected
    );
    if (canonicalMatch) {
      return canonicalMatch;
    }

    // Handle canonical-name drift without hardcoded aliases.
    // Example: "United Kingdom" <-> "United Kingdom of Great Britain and Northern Ireland".
    const containmentMatch = Object.keys(countryRegionsMap).find((key) => {
      const canonicalKey = canonicalCountryKey(key);
      return canonicalKey.includes(canonicalSelected) || canonicalSelected.includes(canonicalKey);
    });

    return containmentMatch || "";
  }, [countryRegionsMap]);
  const getRegionsForCountry = useCallback((country) => {
    const resolvedCountryKey = resolveCountryKey(country);
    return resolvedCountryKey ? countryRegionsMap[resolvedCountryKey] || [] : [];
  }, [countryRegionsMap, resolveCountryKey]);
  const allowedSelectedCountries = selectedAllowedCountries.filter(Boolean);
  const deniedSelectedCountries = selectedDeniedCountries.filter(Boolean);

  const hasAllowedCountrySelection = allowedSelectedCountries.length > 0;
  const hasDeniedCountrySelection = deniedSelectedCountries.length > 0;

  const allowedRegionSourceCountries = allowedSelectedCountries;
  const deniedRegionSourceCountries = hasDeniedCountrySelection ? deniedSelectedCountries : allowedSelectedCountries;
  const hasDeniedRegionCountrySelection = deniedRegionSourceCountries.length > 0;

  const allowedRegionOptions = useMemo(() => {
    const selectedCountryRegions = allowedRegionSourceCountries.flatMap(
      (country) => getRegionsForCountry(country)
    );
    const baseOptions = selectedCountryRegions.length > 0 ? selectedCountryRegions : [];
    return Array.from(new Set([...baseOptions, ...selectedAllowedRegions]));
  }, [allowedRegionSourceCountries, getRegionsForCountry, selectedAllowedRegions]);
  const deniedRegionOptions = useMemo(() => {
    const selectedCountryRegions = deniedRegionSourceCountries.flatMap(
      (country) => getRegionsForCountry(country)
    );
    const baseOptions = selectedCountryRegions.length > 0 ? selectedCountryRegions : [];
    return Array.from(new Set([...baseOptions, ...selectedDeniedRegions]));
  }, [deniedRegionSourceCountries, getRegionsForCountry, selectedDeniedRegions]);
  const filteredAllowedRegions = useMemo(
    () => allowedRegionOptions.filter((region) => region.toLowerCase().includes(allowedRegionsSearch.toLowerCase())),
    [allowedRegionOptions, allowedRegionsSearch]
  );
  const filteredDeniedRegions = useMemo(
    () => deniedRegionOptions.filter((region) => region.toLowerCase().includes(deniedRegionsSearch.toLowerCase())),
    [deniedRegionOptions, deniedRegionsSearch]
  );
  const filteredAllowedFileTypes = useMemo(
    () => fileTypeOptions.filter((type) => type.toLowerCase().includes(allowedFileTypesSearch.toLowerCase())),
    [fileTypeOptions, allowedFileTypesSearch]
  );
  const filteredDeniedFileTypes = useMemo(
    () => fileTypeOptions.filter((type) => type.toLowerCase().includes(deniedFileTypesSearch.toLowerCase())),
    [fileTypeOptions, deniedFileTypesSearch]
  );

  useEffect(() => {
    if (!hasAllowedCountrySelection) {
      if (selectedAllowedRegions.length > 0) {
        setSelectedAllowedRegions([]);
        setHasUnsavedChanges(true);
      }
      setIsAllowedRegionsDropdownOpen(false);
      setAllowedRegionsSearch("");
    }
  }, [hasAllowedCountrySelection, selectedAllowedRegions.length]);

  useEffect(() => {
    if (!hasDeniedRegionCountrySelection) {
      if (selectedDeniedRegions.length > 0) {
        setSelectedDeniedRegions([]);
        setHasUnsavedChanges(true);
      }
      setIsDeniedRegionsDropdownOpen(false);
      setDeniedRegionsSearch("");
    }
  }, [hasDeniedRegionCountrySelection, selectedDeniedRegions.length]);

  // Prevent stale region selections from being saved when a country slot changes.
  // (Regions are stored as a flat list, so we must prune them based on currently selected countries.)
  useEffect(() => {
    if (Object.keys(countryRegionsMap).length === 0) return;
    const coveredAllowedRegions = new Set(
      selectedAllowedCountries.filter(Boolean).flatMap((country) => getRegionsForCountry(country))
    );

    setSelectedAllowedRegions((prev) => {
      const next = prev.filter((region) => coveredAllowedRegions.has(region));
      if (next.length !== prev.length) setHasUnsavedChanges(true);
      return next;
    });
  }, [selectedAllowedCountries, countryRegionsMap, getRegionsForCountry]);

  useEffect(() => {
    if (Object.keys(countryRegionsMap).length === 0) return;
    const coveredDeniedRegions = new Set(
      selectedDeniedCountries.filter(Boolean).flatMap((country) => getRegionsForCountry(country))
    );

    setSelectedDeniedRegions((prev) => {
      const next = prev.filter((region) => coveredDeniedRegions.has(region));
      if (next.length !== prev.length) setHasUnsavedChanges(true);
      return next;
    });
  }, [selectedDeniedCountries, countryRegionsMap, getRegionsForCountry]);
  const getSummaryText = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return "";
    return items.join(", ");
  };
  const getTriggerDisplayText = (items = [], emptyLabel) => {
    if (!Array.isArray(items) || items.length === 0) return emptyLabel;
    return getSummaryText(items);
  };
  const getTriggerTooltip = (items = [], emptyLabel, suffixLabel) => {
    if (!Array.isArray(items) || items.length === 0) return emptyLabel;
    return `${items.join(", ")} (${items.length} ${suffixLabel})`;
  };
  const toStringArray = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item || "").trim())
        .filter(Boolean);
    }
    if (value instanceof Set) {
      return Array.from(value)
        .map((item) => String(item || "").trim())
        .filter(Boolean);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  useEffect(() => {
    const config = spaceConfig || {};

    const allowedCountries = toStringArray(config.allowedCountries);
    const deniedCountries = toStringArray(config.deniedCountries);
    const allowedRegions = toStringArray(config.allowedRegions);
    const deniedRegions = toStringArray(config.deniedRegions);
    const allowedIps = toStringArray(config.allowedIps);
    const deniedIps = toStringArray(config.deniedIps);
    const allowedFileTypes = toStringArray(config.allowedFileTypes);
    const deniedFileTypes = toStringArray(config.deniedFileTypes);
    const maxFileSize = formatBytesToSizeLabel(config.maxFileSize, fileSizeOptions);
    const maxSpaceSize = formatBytesToSizeLabel(config.maxSpaceSize, spaceSizeOptions);

    setSelectedAllowedCountries(allowedCountries);
    setSelectedDeniedCountries(deniedCountries);
    setSelectedAllowedRegions(allowedRegions);
    setSelectedDeniedRegions(deniedRegions);
    setAllowedIpsInput(allowedIps.join(", "));
    setDeniedIpsInput(deniedIps.join(", "));
    setSelectedAllowedFileTypes(
      dedupeStringsPreserveOrder(mapMimeTypesToFileTypes(allowedFileTypes))
    );
    setSelectedDeniedFileTypes(
      dedupeStringsPreserveOrder(mapMimeTypesToFileTypes(deniedFileTypes))
    );
    setSelectedMaxFileSize(maxFileSize);
    setSelectedMaxSpaceSize(maxSpaceSize);

    const dynamicFilters = [];
    if (allowedCountries.length > 0) dynamicFilters.push("Allowed Countries");
    if (deniedCountries.length > 0) dynamicFilters.push("Denied Countries");
    if (allowedRegions.length > 0) dynamicFilters.push("Allowed Regions");
    if (deniedRegions.length > 0) dynamicFilters.push("Denied Regions");
    if (allowedIps.length > 0) dynamicFilters.push("Allowed Ips");
    if (deniedIps.length > 0) dynamicFilters.push("Denied Ips");
    if (allowedFileTypes.length > 0) dynamicFilters.push("Allowed File Types");
    if (deniedFileTypes.length > 0) dynamicFilters.push("Denied File Types");
    if (maxFileSize) dynamicFilters.push("Max File Size");
    // if (maxSpaceSize) dynamicFilters.push("Max Space Size");
    setSelectedFilterOptions(dynamicFilters);

    setHasUnsavedChanges(false);
  }, [spaceName, spaceConfig]);

  const updateAllowedCountriesMenuPosition = () => {
    if (!allowedCountriesTriggerRef.current) return;
    const rect = allowedCountriesTriggerRef.current.getBoundingClientRect();
    setAllowedCountriesMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };
  const updateDeniedCountriesMenuPosition = () => {
    if (!deniedCountriesTriggerRef.current) return;
    const rect = deniedCountriesTriggerRef.current.getBoundingClientRect();
    setDeniedCountriesMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };
  const updateAllowedRegionsMenuPosition = () => {
    if (!allowedRegionsTriggerRef.current) return;
    const rect = allowedRegionsTriggerRef.current.getBoundingClientRect();
    setAllowedRegionsMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };
  const updateDeniedRegionsMenuPosition = () => {
    if (!deniedRegionsTriggerRef.current) return;
    const rect = deniedRegionsTriggerRef.current.getBoundingClientRect();
    setDeniedRegionsMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };
  const updateAllowedFileTypesMenuPosition = () => {
    if (!allowedFileTypesTriggerRef.current) return;
    const rect = allowedFileTypesTriggerRef.current.getBoundingClientRect();
    setAllowedFileTypesMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };
  const updateDeniedFileTypesMenuPosition = () => {
    if (!deniedFileTypesTriggerRef.current) return;
    const rect = deniedFileTypesTriggerRef.current.getBoundingClientRect();
    setDeniedFileTypesMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };
  const updateMaxFileSizeMenuPosition = () => {
    if (!maxFileSizeTriggerRef.current) return;
    const rect = maxFileSizeTriggerRef.current.getBoundingClientRect();
    setMaxFileSizeMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };
  const updateMaxSpaceSizeMenuPosition = () => {
    if (!maxSpaceSizeTriggerRef.current) return;
    const rect = maxSpaceSizeTriggerRef.current.getBoundingClientRect();
    setMaxSpaceSizeMenuStyle(getAdaptiveMenuStyle(rect, 240));
  };

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    updateTopDropdownMenuPosition();

    window.addEventListener("resize", updateTopDropdownMenuPosition);
    window.addEventListener("scroll", updateTopDropdownMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateTopDropdownMenuPosition);
      window.removeEventListener("scroll", updateTopDropdownMenuPosition, true);
    };
  }, [isDropdownOpen]);
  useEffect(() => {
    if (!isAllowedCountriesDropdownOpen) return undefined;
    updateAllowedCountriesMenuPosition();

    window.addEventListener("resize", updateAllowedCountriesMenuPosition);
    window.addEventListener("scroll", updateAllowedCountriesMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateAllowedCountriesMenuPosition);
      window.removeEventListener("scroll", updateAllowedCountriesMenuPosition, true);
    };
  }, [isAllowedCountriesDropdownOpen]);
  useEffect(() => {
    if (!isDeniedCountriesDropdownOpen) return undefined;
    updateDeniedCountriesMenuPosition();

    window.addEventListener("resize", updateDeniedCountriesMenuPosition);
    window.addEventListener("scroll", updateDeniedCountriesMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateDeniedCountriesMenuPosition);
      window.removeEventListener("scroll", updateDeniedCountriesMenuPosition, true);
    };
  }, [isDeniedCountriesDropdownOpen]);
  useEffect(() => {
    if (!isAllowedRegionsDropdownOpen) return undefined;
    updateAllowedRegionsMenuPosition();

    window.addEventListener("resize", updateAllowedRegionsMenuPosition);
    window.addEventListener("scroll", updateAllowedRegionsMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateAllowedRegionsMenuPosition);
      window.removeEventListener("scroll", updateAllowedRegionsMenuPosition, true);
    };
  }, [isAllowedRegionsDropdownOpen]);
  useEffect(() => {
    if (!isDeniedRegionsDropdownOpen) return undefined;
    updateDeniedRegionsMenuPosition();

    window.addEventListener("resize", updateDeniedRegionsMenuPosition);
    window.addEventListener("scroll", updateDeniedRegionsMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateDeniedRegionsMenuPosition);
      window.removeEventListener("scroll", updateDeniedRegionsMenuPosition, true);
    };
  }, [isDeniedRegionsDropdownOpen]);
  useEffect(() => {
    if (!isAllowedFileTypesDropdownOpen) return undefined;
    updateAllowedFileTypesMenuPosition();

    window.addEventListener("resize", updateAllowedFileTypesMenuPosition);
    window.addEventListener("scroll", updateAllowedFileTypesMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateAllowedFileTypesMenuPosition);
      window.removeEventListener("scroll", updateAllowedFileTypesMenuPosition, true);
    };
  }, [isAllowedFileTypesDropdownOpen]);
  useEffect(() => {
    if (!isDeniedFileTypesDropdownOpen) return undefined;
    updateDeniedFileTypesMenuPosition();

    window.addEventListener("resize", updateDeniedFileTypesMenuPosition);
    window.addEventListener("scroll", updateDeniedFileTypesMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateDeniedFileTypesMenuPosition);
      window.removeEventListener("scroll", updateDeniedFileTypesMenuPosition, true);
    };
  }, [isDeniedFileTypesDropdownOpen]);
  useEffect(() => {
    if (!isMaxFileSizeDropdownOpen) return undefined;
    updateMaxFileSizeMenuPosition();

    window.addEventListener("resize", updateMaxFileSizeMenuPosition);
    window.addEventListener("scroll", updateMaxFileSizeMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMaxFileSizeMenuPosition);
      window.removeEventListener("scroll", updateMaxFileSizeMenuPosition, true);
    };
  }, [isMaxFileSizeDropdownOpen]);
  useEffect(() => {
    if (!isMaxSpaceSizeDropdownOpen) return undefined;
    updateMaxSpaceSizeMenuPosition();

    window.addEventListener("resize", updateMaxSpaceSizeMenuPosition);
    window.addEventListener("scroll", updateMaxSpaceSizeMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMaxSpaceSizeMenuPosition);
      window.removeEventListener("scroll", updateMaxSpaceSizeMenuPosition, true);
    };
  }, [isMaxSpaceSizeDropdownOpen]);

  // Console log current displayed permissions
  // console.log("👁️ SpacePermissionsTable - Currently displaying permissions:", {
  //   spaceName: spaceName || "No space selected",
  //   filteredPermissions: filteredPermissions,
  //   allPermissions: permissions
  // });

  const allowedCountrySlotsForUI = selectedAllowedCountries.length > 0 ? selectedAllowedCountries : [""];
  const deniedCountrySlotsForUI = selectedDeniedCountries.length > 0 ? selectedDeniedCountries : [""];
  const getSelectedRegionCountForCountry = (country, selectedRegions = []) => {
    if (!country) return 0;
    const regionsForCountry = getRegionsForCountry(country);
    if (regionsForCountry.length === 0) return 0;
    return regionsForCountry.filter((region) => selectedRegions.includes(region)).length;
  };

  return (
    <div className="Card SpacePermissionsTable">
      <div className="CardHeader">
        <div className="HeaderTitle">
          <h3>Space Permissions</h3>
          {spaceName && (
            <span className="SpaceName">for &quot;{spaceName}&quot;</span>
          )}
        </div>
        <div className="Toolbar ToolbarPrimary">
          <div className="MultiSelectDropdown" ref={dropdownRef}>
            <button
              type="button"
              ref={dropdownTriggerRef}
              className={`DropdownTrigger TopFilterTrigger ${isDropdownOpen ? "open" : ""} ${selectedFilterOptions.length > 0 ? "hasValue" : ""} ${selectedFilterOptions.length === 0 ? "needsAttention" : ""}`}
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              disabled={!spaceName}
            >
              {selectedFilterOptions.length > 0
                ? `${selectedFilterOptions.length} selected`
                : "Select permissions"}
            </button>
            {isDropdownOpen && createPortal(
              <div
                className="DropdownMenu DropdownMenuPortal"
                ref={dropdownMenuRef}
                style={topDropdownMenuStyle}
              >
                <input
                  type="text"
                  className="DropdownSearchInput"
                  placeholder="Search options..."
                  value={topDropdownSearch}
                  onChange={(e) => setTopDropdownSearch(e.target.value)}
                />
                {filteredTopOptions.map((option) => (
                  <label key={option} className="DropdownOption">
                    <input
                      type="checkbox"
                      checked={selectedFilterOptions.includes(option)}
                      onChange={() => toggleFilterOption(option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>
        <button
          className={`PrimarySm ${hasUnsavedChanges ? "hasChanges" : ""}`}
          onClick={handleSavePermissions}
          disabled={!hasUnsavedChanges || isSaving}
        >
          {isSaving ? "Saving..." : "Save Permissions"}
        </button>
      </div>
      <div className="CardBody">
        <div className="PermissionsTableContainer">
          <table className="PermissionsTable">
            <thead className="TableHeader">
              <tr>
                <th className="NameColumn">Permission Name</th>
                <th className="SelectedPermissionColumn">Selected Permission</th>
                <th className="ActionColumn">Actions</th>
              </tr>
            </thead>
            <tbody className="TableBody">
              {filteredPermissions.length > 0 ? (
                <>
                  {filteredPermissions.map((permission) => (
                    <tr key={permission.id} className="TableRow">
                      <td className="NameColumn">{permission.name}</td>
                      <td className="SelectedPermissionColumn">-</td>
                      <td className="ActionColumn">
                        <div className="PermissionCheckboxes">
                          <label className="CheckboxLabel">
                            <input
                              type="checkbox"
                              checked={permission.read}
                              onChange={() => handlePermissionChange(permission.id, "read")}
                            />
                            <span>Read</span>
                          </label>
                          <label className="CheckboxLabel">
                            <input
                              type="checkbox"
                              checked={permission.write}
                              onChange={() => handlePermissionChange(permission.id, "write")}
                            />
                            <span>Write</span>
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {selectedFilterOptions.includes("Allowed Countries") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Allowed Countries</td>
                      <td className="SelectedPermissionColumn">
                        <div className="CountriesRegionsScroller CountriesRegionsScrollerAllowed">
                          <div className="CountriesRegionCards">
                            {allowedCountrySlotsForUI.map((slotCountry, slotIdx) => {
                              const deniedSet = new Set(selectedDeniedCountries.filter(Boolean));
                              const otherAllowedSet = new Set(
                                selectedAllowedCountries.filter((c, idx) => Boolean(c) && idx !== slotIdx)
                              );

                              const allowedCountryOptionsBase = availableCountries.filter(
                                (country) => !deniedSet.has(country) && !otherAllowedSet.has(country)
                              );
                              const allowedCountryOptions = slotCountry && !allowedCountryOptionsBase.includes(slotCountry)
                                ? [slotCountry, ...allowedCountryOptionsBase]
                                : allowedCountryOptionsBase;

                              return (
                                <div
                                  className="CountryRegionRow"
                                  key={`allowed-country-slot-${slotIdx}`}
                                >
                                  <div className="CountrySlotCard">
                                    <div className="CountrySlotTitle">Allowed country</div>
                                    <button
                                      type="button"
                                      className="DropdownTrigger CountryPickerTrigger"
                                      ref={(el) => {
                                        countryPickerTriggerRefs.current[`allowed-${slotIdx}`] = el;
                                      }}
                                      onClick={() => {
                                        if (openCountryPicker?.kind === "allowed" && openCountryPicker?.idx === slotIdx) {
                                          setOpenCountryPicker(null);
                                          setCountryPickerSearch("");
                                          return;
                                        }
                                        setCountryPickerSearch("");
                                        openCountryPickerFor("allowed", slotIdx);
                                      }}
                                      title={slotCountry || "Select country"}
                                    >
                                      {slotCountry || "Select country"}
                                    </button>
                                  </div>

                                  <div className="RegionsSlotCard">
                                    <div className="RegionsSlotTitle">Allowed regions</div>
                                    {!slotCountry ? (
                                      <div className="RegionsSlotEmpty" />
                                    ) : Object.keys(countryRegionsMap).length === 0 ? (
                                      <div className="RegionsSlotEmpty">Loading regions...</div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="DropdownTrigger RegionsPickerTrigger"
                                        ref={(el) => {
                                          regionsPickerTriggerRefs.current[`allowed-${slotIdx}`] = el;
                                        }}
                                        onClick={() => {
                                          if (openRegionsPicker?.kind === "allowed" && openRegionsPicker?.idx === slotIdx) {
                                            setOpenRegionsPicker(null);
                                            setRegionsPickerSearch("");
                                            return;
                                          }
                                          setRegionsPickerSearch("");
                                          openRegionsPickerFor("allowed", slotIdx);
                                        }}
                                      >
                                        {getSelectedRegionCountForCountry(slotCountry, selectedAllowedRegions) > 0
                                          ? `${getSelectedRegionCountForCountry(slotCountry, selectedAllowedRegions)} selected`
                                          : "Select regions"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <button
                            type="button"
                            className="AddCountrySlotBtn"
                            onClick={handleAddAllowedCountrySlot}
                          >
                            +1 Add country
                          </button>
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )}
                  {selectedFilterOptions.includes("Denied Countries") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Denied Countries</td>
                      <td className="SelectedPermissionColumn">
                        <div className="CountriesRegionsScroller CountriesRegionsScrollerDenied">
                          <div className="CountriesRegionCards">
                            {deniedCountrySlotsForUI.map((slotCountry, slotIdx) => {
                              const allowedSet = new Set(selectedAllowedCountries.filter(Boolean));
                              const otherDeniedSet = new Set(
                                selectedDeniedCountries.filter((c, idx) => Boolean(c) && idx !== slotIdx)
                              );

                              const deniedCountryOptionsBase = availableCountries.filter(
                                (country) => !allowedSet.has(country) && !otherDeniedSet.has(country)
                              );
                              const deniedCountryOptions = slotCountry && !deniedCountryOptionsBase.includes(slotCountry)
                                ? [slotCountry, ...deniedCountryOptionsBase]
                                : deniedCountryOptionsBase;

                              return (
                                <div
                                  className="CountryRegionRow"
                                  key={`denied-country-slot-${slotIdx}`}
                                >
                                  <div className="CountrySlotCard">
                                    <div className="CountrySlotTitle">Denied country</div>
                                    <button
                                      type="button"
                                      className="DropdownTrigger CountryPickerTrigger"
                                      ref={(el) => {
                                        countryPickerTriggerRefs.current[`denied-${slotIdx}`] = el;
                                      }}
                                      onClick={() => {
                                        if (openCountryPicker?.kind === "denied" && openCountryPicker?.idx === slotIdx) {
                                          setOpenCountryPicker(null);
                                          setCountryPickerSearch("");
                                          return;
                                        }
                                        setCountryPickerSearch("");
                                        openCountryPickerFor("denied", slotIdx);
                                      }}
                                      title={slotCountry || "Select country"}
                                    >
                                      {slotCountry || "Select country"}
                                    </button>
                                  </div>

                                  <div className="RegionsSlotCard">
                                    <div className="RegionsSlotTitle">Denied regions</div>
                                    {!slotCountry ? (
                                      <div className="RegionsSlotEmpty" />
                                    ) : Object.keys(countryRegionsMap).length === 0 ? (
                                      <div className="RegionsSlotEmpty">Loading regions...</div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="DropdownTrigger RegionsPickerTrigger"
                                        ref={(el) => {
                                          regionsPickerTriggerRefs.current[`denied-${slotIdx}`] = el;
                                        }}
                                        onClick={() => {
                                          if (openRegionsPicker?.kind === "denied" && openRegionsPicker?.idx === slotIdx) {
                                            setOpenRegionsPicker(null);
                                            setRegionsPickerSearch("");
                                            return;
                                          }
                                          setRegionsPickerSearch("");
                                          openRegionsPickerFor("denied", slotIdx);
                                        }}
                                      >
                                        {getSelectedRegionCountForCountry(slotCountry, selectedDeniedRegions) > 0
                                          ? `${getSelectedRegionCountForCountry(slotCountry, selectedDeniedRegions)} selected`
                                          : "Select regions"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <button
                            type="button"
                            className="AddCountrySlotBtn"
                            onClick={handleAddDeniedCountrySlot}
                          >
                            +1 Add country
                          </button>
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )}
                  {selectedFilterOptions.includes("Allowed Ips") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Allowed Ips</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <input
                            type="text"
                            className="SearchInput"
                            placeholder="Type allowed ips"
                            value={allowedIpsInput}
                            onChange={(e) => {
                              const nextValue = sanitizeIpv4Input(e.target.value);
                              if (!isValidIpv4DraftInput(nextValue)) return;
                              setAllowedIpsInput(nextValue);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )}
                  {/* {selectedFilterOptions.includes("Allowed Regions") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Allowed Regions</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <button
                            type="button"
                            ref={allowedRegionsTriggerRef}
                            className={`DropdownTrigger ${isAllowedRegionsDropdownOpen ? "open" : ""}`}
                            onClick={() => {
                              if (!hasAllowedCountrySelection) return;
                              setIsAllowedRegionsDropdownOpen((prev) => !prev);
                            }}
                            disabled={!hasAllowedCountrySelection}
                            title={
                              hasAllowedCountrySelection
                                ? getTriggerTooltip(selectedAllowedRegions, "Select regions", "regions selected")
                                : "Select allowed countries first"
                            }
                          >
                            {hasAllowedCountrySelection
                              ? getTriggerDisplayText(selectedAllowedRegions, "Select regions", "regions selected")
                              : "Select allowed countries first"}
                          </button>
                          {isAllowedRegionsDropdownOpen && createPortal(
                            <div
                              className="DropdownMenu DropdownMenuPortal"
                              ref={allowedRegionsMenuRef}
                              style={allowedRegionsMenuStyle}
                            >
                              <input
                                type="text"
                                className="DropdownSearchInput"
                                placeholder="Search regions..."
                                value={allowedRegionsSearch}
                                onChange={(e) => setAllowedRegionsSearch(e.target.value)}
                              />
                              {!hasAllowedCountrySelection ? (
                                <div className="DropdownOption">
                                  <span>Select allowed countries first</span>
                                </div>
                              ) : filteredAllowedRegions.length > 0 ? (
                                filteredAllowedRegions.map((region) => (
                                  <label key={region} className="DropdownOption">
                                    <input
                                      type="checkbox"
                                      checked={selectedAllowedRegions.includes(region)}
                                      disabled={selectedDeniedRegions.includes(region)}
                                      onChange={() => toggleAllowedRegion(region)}
                                    />
                                    <span>{region}</span>
                                  </label>
                                ))
                              ) : (
                                <div className="DropdownOption">
                                  <span>No regions data available</span>
                                </div>
                              )}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )} */}
                  {/* {selectedFilterOptions.includes("Denied Regions") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Denied Regions</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <button
                            type="button"
                            ref={deniedRegionsTriggerRef}
                            className={`DropdownTrigger ${isDeniedRegionsDropdownOpen ? "open" : ""}`}
                            onClick={() => {
                              if (!hasDeniedRegionCountrySelection) return;
                              setIsDeniedRegionsDropdownOpen((prev) => !prev);
                            }}
                            disabled={!hasDeniedRegionCountrySelection}
                            title={
                              hasDeniedRegionCountrySelection
                                ? getTriggerTooltip(selectedDeniedRegions, "Select regions", "regions selected")
                                : "Select allowed or denied countries first"
                            }
                          >
                            {hasDeniedRegionCountrySelection
                              ? getTriggerDisplayText(selectedDeniedRegions, "Select regions", "regions selected")
                              : "Select allowed or denied countries first"}
                          </button>
                          {isDeniedRegionsDropdownOpen && createPortal(
                            <div
                              className="DropdownMenu DropdownMenuPortal"
                              ref={deniedRegionsMenuRef}
                              style={deniedRegionsMenuStyle}
                            >
                              <input
                                type="text"
                                className="DropdownSearchInput"
                                placeholder="Search regions..."
                                value={deniedRegionsSearch}
                                onChange={(e) => setDeniedRegionsSearch(e.target.value)}
                              />
                              {!hasDeniedRegionCountrySelection ? (
                                <div className="DropdownOption">
                                  <span>Select allowed or denied countries first</span>
                                </div>
                              ) : filteredDeniedRegions.length > 0 ? (
                                filteredDeniedRegions.map((region) => (
                                  <label key={region} className="DropdownOption">
                                    <input
                                      type="checkbox"
                                      checked={selectedDeniedRegions.includes(region)}
                                      disabled={selectedAllowedRegions.includes(region)}
                                      onChange={() => toggleDeniedRegion(region)}
                                    />
                                    <span>{region}</span>
                                  </label>
                                ))
                              ) : (
                                <div className="DropdownOption">
                                  <span>No regions data available</span>
                                </div>
                              )}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )} */}
                  {selectedFilterOptions.includes("Denied Ips") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Denied Ips</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <input
                            type="text"
                            className="SearchInput"
                            placeholder="Type denied ips"
                            value={deniedIpsInput}
                            onChange={(e) => {
                              const nextValue = sanitizeIpv4Input(e.target.value);
                              if (!isValidIpv4DraftInput(nextValue)) return;
                              setDeniedIpsInput(nextValue);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )}
                  {selectedFilterOptions.includes("Allowed File Types") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Allowed File Types</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <button
                            type="button"
                            ref={allowedFileTypesTriggerRef}
                            className={`DropdownTrigger ${isAllowedFileTypesDropdownOpen ? "open" : ""}`}
                            onClick={() => setIsAllowedFileTypesDropdownOpen((prev) => !prev)}
                            title={getTriggerTooltip(selectedAllowedFileTypes, "Select file types", "selected")}
                          >
                            {getTriggerDisplayText(selectedAllowedFileTypes, "Select file types", "selected")}
                          </button>
                          {isAllowedFileTypesDropdownOpen && createPortal(
                            <div
                              className="DropdownMenu DropdownMenuPortal"
                              ref={allowedFileTypesMenuRef}
                              style={allowedFileTypesMenuStyle}
                            >
                              <input
                                type="text"
                                className="DropdownSearchInput"
                                placeholder="Search file types..."
                                value={allowedFileTypesSearch}
                                onChange={(e) => setAllowedFileTypesSearch(e.target.value)}
                              />
                              {filteredAllowedFileTypes.map((type) => (
                                <label key={type} className="DropdownOption">
                                  <input
                                    type="checkbox"
                                    checked={selectedAllowedFileTypes.includes(type)}
                                    disabled={selectedDeniedFileTypes.includes(type)}
                                    onChange={() => toggleAllowedFileType(type)}
                                  />
                                  <span>{type}</span>
                                </label>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )}
                  {selectedFilterOptions.includes("Denied File Types") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Denied File Types</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <button
                            type="button"
                            ref={deniedFileTypesTriggerRef}
                            className={`DropdownTrigger ${isDeniedFileTypesDropdownOpen ? "open" : ""}`}
                            onClick={() => setIsDeniedFileTypesDropdownOpen((prev) => !prev)}
                            title={getTriggerTooltip(selectedDeniedFileTypes, "Select file types", "selected")}
                          >
                            {getTriggerDisplayText(selectedDeniedFileTypes, "Select file types", "selected")}
                          </button>
                          {isDeniedFileTypesDropdownOpen && createPortal(
                            <div
                              className="DropdownMenu DropdownMenuPortal"
                              ref={deniedFileTypesMenuRef}
                              style={deniedFileTypesMenuStyle}
                            >
                              <input
                                type="text"
                                className="DropdownSearchInput"
                                placeholder="Search file types..."
                                value={deniedFileTypesSearch}
                                onChange={(e) => setDeniedFileTypesSearch(e.target.value)}
                              />
                              {filteredDeniedFileTypes.map((type) => (
                                <label key={type} className="DropdownOption">
                                  <input
                                    type="checkbox"
                                    checked={selectedDeniedFileTypes.includes(type)}
                                    disabled={selectedAllowedFileTypes.includes(type)}
                                    onChange={() => toggleDeniedFileType(type)}
                                  />
                                  <span>{type}</span>
                                </label>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )}
                  {selectedFilterOptions.includes("Max File Size") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Max File Size</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <button
                            type="button"
                            ref={maxFileSizeTriggerRef}
                            className={`DropdownTrigger ${isMaxFileSizeDropdownOpen ? "open" : ""}`}
                            onClick={() => setIsMaxFileSizeDropdownOpen((prev) => !prev)}
                          >
                            {selectedMaxFileSize || "Select max file size"}
                          </button>
                          {isMaxFileSizeDropdownOpen && createPortal(
                            <div
                              className="DropdownMenu DropdownMenuPortal"
                              ref={maxFileSizeMenuRef}
                              style={maxFileSizeMenuStyle}
                            >
                              {fileSizeOptions.map((size) => (
                                <label key={size} className="DropdownOption">
                                  <input
                                    type="radio"
                                    name="maxFileSize"
                                    checked={selectedMaxFileSize === size}
                                    onChange={() => {
                                      setSelectedMaxFileSize(size);
                                      setIsMaxFileSizeDropdownOpen(false);
                                      setHasUnsavedChanges(true);
                                    }}
                                  />
                                  <span>{size}</span>
                                </label>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )}
                  {/* {selectedFilterOptions.includes("Max Space Size") && (
                    <tr className="TableRow">
                      <td className="NameColumn">Max Space Size</td>
                      <td className="SelectedPermissionColumn">
                        <div className="InlineMultiSelectDropdown">
                          <button
                            type="button"
                            ref={maxSpaceSizeTriggerRef}
                            className={`DropdownTrigger ${isMaxSpaceSizeDropdownOpen ? "open" : ""}`}
                            onClick={() => setIsMaxSpaceSizeDropdownOpen((prev) => !prev)}
                          >
                            {selectedMaxSpaceSize || "Select max space size"}
                          </button>
                          {isMaxSpaceSizeDropdownOpen && createPortal(
                            <div
                              className="DropdownMenu DropdownMenuPortal"
                              ref={maxSpaceSizeMenuRef}
                              style={maxSpaceSizeMenuStyle}
                            >
                              {spaceSizeOptions.map((size) => (
                                <label key={size} className="DropdownOption">
                                  <input
                                    type="radio"
                                    name="maxSpaceSize"
                                    checked={selectedMaxSpaceSize === size}
                                    onChange={() => {
                                      setSelectedMaxSpaceSize(size);
                                      setIsMaxSpaceSizeDropdownOpen(false);
                                      setHasUnsavedChanges(true);
                                    }}
                                  />
                                  <span>{size}</span>
                                </label>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td className="ActionColumn" />
                    </tr>
                  )} */}
                </>
              ) : (
                <tr className="TableRow">
                  <td colSpan="3" className="EmptyState">
                    {searchQuery ? "No permissions found matching your search" : "No permissions configured for this space"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {openCountryPicker && createPortal(
        <div
          className="DropdownMenu DropdownMenuPortal CountryPickerMenu"
          ref={countryPickerMenuRef}
          style={countryPickerMenuStyle}
        >
          <input
            type="text"
            className="DropdownSearchInput"
            placeholder="Search countries..."
            value={countryPickerSearch}
            onChange={(e) => setCountryPickerSearch(e.target.value)}
          />
          {(() => {
            const { kind, idx } = openCountryPicker;
            const slotCountry = kind === "allowed"
              ? (allowedCountrySlotsForUI[idx] || "")
              : (deniedCountrySlotsForUI[idx] || "");

            const forbiddenSet = new Set(
              kind === "allowed"
                ? selectedDeniedCountries.filter(Boolean)
                : selectedAllowedCountries.filter(Boolean)
            );
            const ownOtherSet = new Set(
              (kind === "allowed" ? selectedAllowedCountries : selectedDeniedCountries)
                .filter((c, i) => Boolean(c) && i !== idx)
            );

            const baseOptions = availableCountries.filter((country) => {
              if (forbiddenSet.has(country)) return false;
              if (ownOtherSet.has(country)) return false;
              if (!countryPickerSearch) return true;
              return country.toLowerCase().includes(countryPickerSearch.toLowerCase());
            });

            const options = slotCountry && !baseOptions.includes(slotCountry)
              ? [slotCountry, ...baseOptions]
              : baseOptions;

            return (
              <>
                <label key={`clear-country-${kind}-${idx}`} className="DropdownOption">
                  <input
                    type="radio"
                    name={`country-${kind}-${idx}`}
                    checked={slotCountry === ""}
                    onChange={() => {
                      if (kind === "allowed") handleAllowedCountrySlotChange(idx, "");
                      else handleDeniedCountrySlotChange(idx, "");
                      setOpenCountryPicker(null);
                      setCountryPickerSearch("");
                    }}
                  />
                  <span>Clear selection</span>
                </label>
                {options.length > 0 ? options.map((country) => (
                  <label key={country} className="DropdownOption">
                    <input
                      type="radio"
                      name={`country-${kind}-${idx}`}
                      checked={slotCountry === country}
                      onChange={() => {
                        if (kind === "allowed") handleAllowedCountrySlotChange(idx, country);
                        else handleDeniedCountrySlotChange(idx, country);
                        setOpenCountryPicker(null);
                        setCountryPickerSearch("");
                      }}
                    />
                    <span>{country}</span>
                  </label>
                )) : (
                  <div className="DropdownOption">
                    <span>No countries found</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}
      {openRegionsPicker && createPortal(
        <div
          className="DropdownMenu DropdownMenuPortal RegionsPickerMenu"
          ref={regionsPickerMenuRef}
          style={regionsPickerMenuStyle}
        >
          <input
            type="text"
            className="DropdownSearchInput"
            placeholder="Search regions..."
            value={regionsPickerSearch}
            onChange={(e) => setRegionsPickerSearch(e.target.value)}
          />
          {(() => {
            const { kind, idx } = openRegionsPicker;
            const slotCountry = kind === "allowed"
              ? (allowedCountrySlotsForUI[idx] || "")
              : (deniedCountrySlotsForUI[idx] || "");
            const regions = slotCountry ? getRegionsForCountry(slotCountry) : [];
            const filtered = regions.filter((region) =>
              region.toLowerCase().includes(regionsPickerSearch.toLowerCase())
            );

            if (!slotCountry) {
              return (
                <div className="DropdownOption">
                  <span>Select country first</span>
                </div>
              );
            }

            if (filtered.length === 0) {
              return (
                <div className="DropdownOption">
                  <span>No regions found</span>
                </div>
              );
            }

            return filtered.map((region) => (
              <label key={region} className="DropdownOption">
                <input
                  type="checkbox"
                  checked={kind === "allowed" ? selectedAllowedRegions.includes(region) : selectedDeniedRegions.includes(region)}
                  disabled={kind === "allowed" ? selectedDeniedRegions.includes(region) : selectedAllowedRegions.includes(region)}
                  onChange={() => {
                    if (kind === "allowed") toggleAllowedRegion(region);
                    else toggleDeniedRegion(region);
                  }}
                />
                <span>{region}</span>
              </label>
            ));
          })()}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SpacePermissionsTable;
